// packages/utils/src/currency.ts
//
// formatDual() — cross-phase Merchant Preferred Currency Display helper.
//
// Per CONTEXT.md (Phase 3.2 Round 2 — Merchant Preferred Currency
// Display). Centralized formatter so that Phase 3.2 Wallet/Topup/Withdraw/
// Transfer/Tx-history/Gateway/Billing surfaces and downstream Phase 3.3+
// consumers (Order list/detail, Invoice, Auto-Pay summary) all render the
// same dual-currency layout — never duplicate the format logic per phase.
//
// Display contract (locked here, consumed by 3.3 / 3.5):
//   primary   — always USD ($1,234.56), Intl.NumberFormat en-US
//   secondary — "≈ €1,143.11" using preferred currency × admin-entered rate
//               (rate stored as rate_micro / 10^6, i.e. 920000 == 0.92 EUR/USD)
//
// Edge cases:
//   - preferredCurrency === "USD"    → secondary = "" (no redundant duplicate)
//   - preferredCurrency rate missing → secondary = "≈ —"
//   - ratesTable supports BOTH shapes:
//       flat:   { EUR: 920000, GBP: 790000 }
//       nested: { paypal: { EUR: 920000 }, stripe: { EUR: 925000 } }
//     Phase 3.2 consumers pass nested (per-gateway display rates); Phase 3.3+
//     may pass flat (single-rate map). Resolution scans nested values when
//     flat lookup misses — first match wins.
//
// Rounding: HALF_EVEN (banker's rounding). Standard `Intl.NumberFormat` /
// `Math.round` use HALF_AWAY_FROM_ZERO which biases sums upward.
// requires HALF_EVEN per the locked display contract — implemented as a
// scaled-integer helper below to avoid relying on the not-yet-universally-
// supported `roundingMode: "halfEven"` option in Intl.NumberFormat.

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Flat rates table — preferred currency code → rate_micro (rate × 10^6).
 * Example: `{ EUR: 920000, GBP: 790000 }` means 1 USD = 0.92 EUR / 0.79 GBP.
 */
export type FlatRatesTable = Readonly<Record<string, number>>;

/**
 * Nested rates table — gateway → currency → rate_micro. Phase 3.2 stores
 * per-gateway display rates (PayPal vs Stripe may differ slightly). The
 * formatter does not care which gateway — it picks the first available
 * match by scanning gateway buckets.
 */
export type NestedRatesTable = Readonly<Record<string, FlatRatesTable>>;

/** Either shape is acceptable. */
export type RatesTable = FlatRatesTable | NestedRatesTable;

/** Output shape — both lines pre-formatted as display strings. */
export interface FormatDualResult {
  /** Always USD ($1,234.56), even when preferredCurrency === "USD". */
  primary: string;
  /**
   * Either:
   *   "≈ €1,143.11" when a rate is found,
   *   "≈ —"        when preferredCurrency is non-USD but rate missing,
   *   ""           when preferredCurrency === "USD" (no duplicate line).
   */
  secondary: string;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * HALF_EVEN (banker's rounding) for a finite number to integer.
 *
 *   1.5 → 2   (nearest even)
 *   2.5 → 2   (nearest even — different from Math.round which gives 3)
 *   3.5 → 4   (nearest even)
 *   0.5 → 0   (nearest even)
 *  -1.5 → -2  (nearest even, sign-symmetric)
 *  -2.5 → -2  (nearest even)
 */
function roundHalfEvenInt(value: number): number {
  if (!Number.isFinite(value)) return value;
  const floor = Math.floor(value);
  const diff = value - floor;
  // Strictly below 0.5 → round down; strictly above 0.5 → round up.
  if (diff < 0.5) return floor;
  if (diff > 0.5) return floor + 1;
  // Exactly 0.5 — pick the even neighbour.
  // floor % 2 === 0 → floor is even → keep it.
  // floor % 2 !== 0 → ceil to next (which is even).
  return floor % 2 === 0 ? floor : floor + 1;
}

/**
 * HALF_EVEN-rounded cents-style scale: multiplies `value` by 10^digits,
 * banker-rounds to int, divides back to a clean float for Intl.NumberFormat
 * to display without re-rounding ambiguity.
 *
 * For currency display we always format with 2 fraction digits, so we round
 * to the nearest cent (10^2 = 100) under HALF_EVEN before handing the float
 * to Intl. Intl is then asked for `minimumFractionDigits: 2,
 * maximumFractionDigits: 2` and will not introduce a second rounding pass
 * because the input is already an integer number of cents / 100.
 */
function roundHalfEvenToCents(value: number): number {
  if (!Number.isFinite(value)) return value;
  const cents = roundHalfEvenInt(value * 100);
  return cents / 100;
}

/**
 * Look up a rate_micro by currency code. Tries flat shape first
 * (`ratesTable[currency]` is a number) — if not found, scans nested shape
 * values (`ratesTable[gateway][currency]`) and returns the first hit.
 *
 * Returns `null` when no rate is available for the requested currency.
 */
function resolveRateMicro(
  ratesTable: RatesTable | null | undefined,
  currency: string,
): number | null {
  if (!ratesTable) return null;
  // Flat shape: ratesTable[currency] is a number.
  const direct = (ratesTable as Record<string, unknown>)[currency];
  if (typeof direct === 'number' && Number.isFinite(direct) && direct > 0) {
    return direct;
  }
  // Nested shape: scan gateway buckets, return first match.
  for (const key of Object.keys(ratesTable)) {
    const bucket = (ratesTable as Record<string, unknown>)[key];
    if (bucket && typeof bucket === 'object') {
      const nested = (bucket as Record<string, unknown>)[currency];
      if (typeof nested === 'number' && Number.isFinite(nested) && nested > 0) {
        return nested;
      }
    }
  }
  return null;
}

/**
 * Format a USD-cent integer as "$1,234.56" using en-US locale. HALF_EVEN
 * applied at the cent boundary (which for integer cents is a no-op, but
 * keeps the surface consistent if a caller passes fractional cents).
 */
function formatUsd(usdCents: number): string {
  const dollars = roundHalfEvenToCents(usdCents / 100);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

/**
 * Format a converted amount in the preferred currency. Falls back to
 * en-US locale (per task acceptance criteria) so that thousand separators
 * remain `,` and decimal `.` regardless of currency code — keeps the
 * dual-line UX visually aligned with the USD primary above.
 */
function formatPreferred(amount: number, currency: string): string {
  const rounded = roundHalfEvenToCents(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rounded);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Render a USD amount alongside its preferred-currency approximation.
 *
 * @param usdCents          Integer USD cents (positive, zero, negative supported).
 * @param preferredCurrency ISO 4217 code of the merchant's preferred currency.
 *                          When equal to "USD" the secondary line is suppressed.
 * @param ratesTable        Either flat `{ EUR: 920000 }` or nested
 *                          `{ paypal: { EUR: 920000 }, stripe: { EUR: 925000 } }`.
 *                          Values are rate_micro (rate × 10^6).
 *
 * @returns Object with `primary` (always populated) and `secondary` (empty
 *          when preferredCurrency is USD; "≈ —" when rate missing; otherwise
 *          "≈ <converted amount>").
 *
 * @example
 *   formatDual(123456, 'USD', { EUR: 920000 })
 *   // → { primary: "$1,234.56", secondary: "" }
 *
 *   formatDual(123456, 'EUR', { EUR: 920000 })
 *   // → { primary: "$1,234.56", secondary: "≈ €1,135.80" }
 *
 *   formatDual(123456, 'JPY', { EUR: 920000 })
 *   // → { primary: "$1,234.56", secondary: "≈ —" }
 *
 *   formatDual(123456, 'EUR', { paypal: { EUR: 920000 } })
 *   // → { primary: "$1,234.56", secondary: "≈ €1,135.80" }
 */
export function formatDual(
  usdCents: number,
  preferredCurrency: string,
  ratesTable: RatesTable,
): FormatDualResult {
  const primary = formatUsd(usdCents);

  if (preferredCurrency === 'USD') {
    return { primary, secondary: '' };
  }

  const rateMicro = resolveRateMicro(ratesTable, preferredCurrency);
  if (rateMicro === null) {
    return { primary, secondary: '≈ —' };
  }

  // rate_micro / 10^6 = decimal rate; multiply USD dollars by it.
  const usdDollars = usdCents / 100;
  const converted = usdDollars * (rateMicro / 1_000_000);
  return {
    primary,
    secondary: `≈ ${formatPreferred(converted, preferredCurrency)}`,
  };
}
